import { NavLink, useLocation } from "react-router-dom";
import {
	IconHome,
	IconSparkles,
	IconSettings,
	IconNote,
	IconAlarm,
	IconLayoutGrid,
	IconUserHeart,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useApi } from "@/hooks/useApi";
import { api } from "@/lib/api";
import { MenuSheet } from "./MenuSheet";
import { useState } from "react";

type NavItem = { to: string; label: string; icon: ReactNode };

const COUPLE_ITEMS: NavItem[] = [
	{ to: "/dashboard", label: "Beranda", icon: <IconHome size={20} /> },
	{ to: "/quiz", label: "Kuis", icon: <IconSparkles size={20} /> },
	{ to: "/insights", label: "Insight", icon: <IconUserHeart size={20} /> },
];

const SOLO_ITEMS: NavItem[] = [
	{ to: "/dashboard", label: "Beranda", icon: <IconHome size={20} /> },
	{ to: "/notes", label: "Catatan", icon: <IconNote size={20} /> },
	{ to: "/reminders", label: "Pengingat", icon: <IconAlarm size={20} /> },
];

type Slot = { to: string; label: string; icon: ReactNode } | "menu";

function NavItemRender({ item, pathname }: { item: NavItem; pathname: string }) {
	const active =
		item.to === "/dashboard"
			? pathname === "/dashboard" || pathname === "/"
			: pathname.startsWith(item.to);
	return (
		<div className="flex-1">
			<NavLink
				to={item.to}
				className="nav-link flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-semibold no-underline"
				style={{
					color: active ? "var(--color-primary)" : "var(--color-text-muted)",
				}}
			>
				<span
					style={{
						display: "inline-flex",
						padding: "0.25rem 0.75rem",
						borderRadius: "var(--radius-full)",
						background: active ? "var(--color-primary-soft)" : "transparent",
					}}
				>
					{item.icon}
				</span>
				{item.label}
			</NavLink>
		</div>
	);
}

function MenuSlot({ menuOpen, onOpen }: { menuOpen: boolean; onOpen: () => void }) {
	return (
		<div className="flex-1">
			<button
				type="button"
				onClick={onOpen}
				className="nav-link flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-semibold no-underline w-full"
				style={{
					color: menuOpen ? "var(--color-primary)" : "var(--color-text-muted)",
				}}
			>
				<span
					style={{
						display: "inline-flex",
						padding: "0.25rem 0.75rem",
						borderRadius: "var(--radius-full)",
						background: menuOpen ? "var(--color-primary-soft)" : "transparent",
					}}
				>
					<IconLayoutGrid size={20} />
				</span>
				Menu
			</button>
		</div>
	);
}

export function BottomNav() {
	const { isLoaded, isSignedIn } = useAuth();
	const { pathname } = useLocation();
	const [menuOpen, setMenuOpen] = useState(false);
	const meApi = useApi((t) => api.me(t), []);
	const hasCouple = Boolean(meApi.data?.couple?.paired);
	if (!isLoaded || !isSignedIn) return null;
	if (meApi.loading) return null;

	const base = hasCouple ? COUPLE_ITEMS : SOLO_ITEMS;
	const middleIndex = Math.ceil(base.length / 2);

	const slots: Slot[] = [];
	base.forEach((item, index) => {
		if (index === middleIndex) slots.push("menu");
		slots.push(item);
	});
	if (!slots.includes("menu")) slots.splice(middleIndex, 0, "menu");

	return (
		<>
			<nav className="fixed inset-x-0 bottom-0 z-40 pb-[env(safe-area-inset-bottom)]">
				<div className="mx-auto flex max-w-[480px] items-stretch justify-between bg-[color:var(--color-surface)] border-t border-[color:var(--color-border)]">
					{slots.map((slot) => {
						if (slot === "menu") {
							return (
								<MenuSlot
									key="menu"
									menuOpen={menuOpen}
									onOpen={() => setMenuOpen(true)}
								/>
							);
						}
						return (
							<NavItemRender key={slot.to} item={slot} pathname={pathname} />
						);
					})}

					<div className="flex-1">
						<NavLink
							to="/settings"
							className="nav-link flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-semibold no-underline"
							style={{
								color: pathname.startsWith("/settings")
									? "var(--color-primary)"
									: "var(--color-text-muted)",
							}}
						>
							<span
								style={{
									display: "inline-flex",
									padding: "0.25rem 0.75rem",
									borderRadius: "var(--radius-full)",
									background: pathname.startsWith("/settings")
										? "var(--color-primary-soft)"
										: "transparent",
								}}
							>
								<IconSettings size={20} />
							</span>
							Pengaturan
						</NavLink>
					</div>
				</div>
			</nav>
			<MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} hasCouple={hasCouple} />
		</>
	);
}
